import {
    Globe2,
    Search,
    SlidersHorizontal,
    Sparkles,
    X as XIcon,
} from "lucide-react";
import Link from "next/link";
import { useEffect, useMemo, useState } from "react";

import { useAccount, useCurrentUser, useFileUrl, useStoreMatchingItems } from "@/lib/hooks";
import { Guardian, typeDefs } from "@/lib/schema";
import { cn } from "@/lib/util";

type SortKey = "featured" | "recent" | "name";
type ScopeKey = "all" | "mine";

function useDebouncedValue<T>(value: T, delay = 300): T {
    const [debounced, setDebounced] = useState<T>(value);
    useEffect(() => {
        const id = setTimeout(() => setDebounced(value), delay);
        return () => clearTimeout(id);
    }, [value, delay]);
    return debounced;
}

function statusLabel(status?: string) {
    if (!status) return "Unknown";
    const s = status.toLowerCase();
    if (s === "approved") return "Approved";
    if (s === "pending") return "Pending";
    if (s === "denied") return "Denied";
    return status;
}

function StatusChip({ status }: { status?: string }) {
    const s = status?.toLowerCase();
    const colors =
        s === "approved"
            ? "text-[rgba(247,244,239,0.85)] border-[rgba(255,255,255,0.08)] bg-[rgba(255,255,255,0.03)]"
            : s === "pending"
            ? "text-[rgba(214,200,180,0.9)] border-[rgba(214,200,180,0.35)] bg-[rgba(214,200,180,0.12)]"
            : s === "denied"
            ? "text-[rgba(255,160,160,0.9)] border-[rgba(255,160,160,0.35)] bg-[rgba(255,160,160,0.12)]"
            : "text-[rgba(247,244,239,0.7)] border-[rgba(255,255,255,0.12)] bg-[rgba(255,255,255,0.06)]";

    return (
        <span className={cn("inline-flex items-center px-2 py-0.5 text-[10px] rounded-full border", colors)}>
            {statusLabel(status)}
        </span>
    );
}

function GuardianImage({ guardian }: { guardian: Guardian }) {
    const url = useFileUrl(guardian.image_path);
    const initials = useMemo(() => {
        const parts = guardian.name?.trim().split(/\s+/) ?? [];
        return parts.slice(0, 2).map(p => p[0]?.toUpperCase()).join("") || "GC";
    }, [guardian.name]);

    return (
        <div className="aspect-[16/10] w-full rounded-minor overflow-hidden bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.06)]">
            {url === null ? (
                <div className="w-full h-full animate-pulse bg-[rgba(255,255,255,0.04)]" />
            ) : url === undefined ? (
                <div className="w-full h-full flex items-center justify-center">
                    <span className="text-[rgba(247,244,239,0.7)] text-3xl">{initials}</span>
                </div>
            ) : (
                // eslint-disable-next-line @next/next/no-img-element
                <img src={url} alt={guardian.name} className="w-full h-full object-cover" />
            )}
        </div>
    );
}

function GuardianCard({ guardian, showStatus }: { guardian: Guardian; showStatus: boolean }) {
    const topTags = (guardian.tags ?? []).slice(0, 3);

    return (
        <Link href={`/guardian/${guardian.id}`} className="glass card-actionable p-4 rounded-major shadow-glass stack-gap">
            <GuardianImage guardian={guardian} />
            <div className="flex items-center justify-between gap-2">
                <h3 className="gc-heading text-lg jade">{guardian.name}</h3>
                {showStatus && <StatusChip status={guardian.status} />}
            </div>
            {guardian.description && (
                <p className="text-muted line-clamp-2">{guardian.description}</p>
            )}
            {topTags.length > 0 && (
                <div className="flex flex-wrap gap-2 pt-1">
                    {topTags.map(tag => (
                        <span key={tag} className="tag-pill">{tag}</span>
                    ))}
                </div>
            )}
        </Link>
    );
}

function GuardianCardSkeleton() {
    return (
        <div className="glass p-4 rounded-major shadow-glass stack-gap">
            <div className="aspect-[16/10] w-full rounded-minor overflow-hidden bg-[rgba(255,255,255,0.05)] animate-pulse" />
            <div className="flex items-center justify-between gap-2">
                <div className="h-5 w-40 bg-[rgba(255,255,255,0.06)] rounded animate-pulse" />
                <div className="h-4 w-16 bg-[rgba(255,255,255,0.06)] rounded-full animate-pulse" />
            </div>
            <div className="h-4 w-full bg-[rgba(255,255,255,0.06)] rounded animate-pulse" />
            <div className="h-4 w-2/3 bg-[rgba(255,255,255,0.06)] rounded animate-pulse" />
            <div className="flex gap-2 pt-1">
                <div className="h-6 w-16 bg-[rgba(255,255,255,0.06)] rounded-full animate-pulse" />
                <div className="h-6 w-12 bg-[rgba(255,255,255,0.06)] rounded-full animate-pulse" />
            </div>
        </div>
    );
}

export default function GuardiansPage() {
    const user = useCurrentUser();
    const account = useAccount();
    const isSignedIn = user !== undefined && user !== null;

    // Search / filters / sort
    const [search, setSearch] = useState("");
    const debouncedSearch = useDebouncedValue(search, 250);
    const [selectedTags, setSelectedTags] = useState<string[]>([]);
    const [sortKey, setSortKey] = useState<SortKey>("featured");
    const [scope, setScope] = useState<ScopeKey>("all");

    // Data queries
    const publicGuardians = useStoreMatchingItems<Guardian>(
        typeDefs.Guardian,
        { is_public: true, status: "approved" },
        { orderBy: "created_at", orderByDesc: true }
    );

    // Important fix: disable account query cleanly when no account to avoid hook errors
    const accountGuardians = useStoreMatchingItems<Guardian>(
        typeDefs.Guardian,
        account ? { account_id: account.id } : undefined,
        { disabled: !account, orderBy: "created_at", orderByDesc: true, resetOnDisabled: true }
    );

    const loadingPublic = publicGuardians === null;
    const loadingAccount = account ? accountGuardians === null : false;
    const loading = loadingPublic || loadingAccount;

    const canScopeMine = isSignedIn && !!account;

    // Merge and de-duplicate (account-owned may include public items too)
    const mergedGuardians = useMemo<Guardian[]>(() => {
        const pub = Array.isArray(publicGuardians) ? publicGuardians : [];
        const acc = Array.isArray(accountGuardians) ? accountGuardians : [];
        const map = new Map<string, Guardian>();
        for (const g of [...pub, ...acc]) map.set(g.id, g);
        return Array.from(map.values());
    }, [publicGuardians, accountGuardians]);

    // Determine base list by scope
    const baseList = useMemo<Guardian[]>(() => {
        if (canScopeMine && scope === "mine") {
            const acc = Array.isArray(accountGuardians) ? accountGuardians : [];
            return acc;
        }
        return mergedGuardians;
    }, [mergedGuardians, accountGuardians, scope, canScopeMine]);

    // Available tags (from current scope)
    const availableTags = useMemo<string[]>(() => {
        const freq = new Map<string, number>();
        for (const g of baseList) {
            for (const t of g.tags ?? []) {
                const key = t.trim();
                if (!key) continue;
                freq.set(key, (freq.get(key) ?? 0) + 1);
            }
        }
        return Array.from(freq.entries())
            .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
            .map(([t]) => t);
    }, [baseList]);

    // Apply search + tag filters
    const filtered = useMemo<Guardian[]>(() => {
        const term = debouncedSearch.trim().toLowerCase();
        let list = baseList;
        if (term) {
            list = list.filter(g => g.name?.toLowerCase().includes(term) || g.description?.toLowerCase().includes(term));
        }
        if (selectedTags.length > 0) {
            list = list.filter(g => selectedTags.every(t => (g.tags ?? []).includes(t)));
        }
        return list;
    }, [baseList, debouncedSearch, selectedTags]);

    // Sort
    const sorted = useMemo<Guardian[]>(() => {
        const arr = [...filtered];
        if (sortKey === "featured") {
            arr.sort((a, b) => {
                const fa = a.is_featured ? 1 : 0;
                const fb = b.is_featured ? 1 : 0;
                if (fb - fa !== 0) return fb - fa;
                return a.name.localeCompare(b.name);
            });
        } else if (sortKey === "recent") {
            arr.sort((a, b) => {
                const da = Date.parse(a.created_at || "1970-01-01");
                const db = Date.parse(b.created_at || "1970-01-01");
                return db - da;
            });
        } else if (sortKey === "name") {
            arr.sort((a, b) => a.name.localeCompare(b.name));
        }
        return arr;
    }, [filtered, sortKey]);

    const resultsCount = sorted.length;

    function toggleTag(tag: string) {
        setSelectedTags(prev =>
            prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
        );
    }

    function clearAll() {
        setSearch("");
        setSelectedTags([]);
        setSortKey("featured");
        setScope("all");
    }

    return (
        <div className="page--GuardiansPage stack-gap section-gap">
            {/* Header */}
            <section className="stack-gap">
                <div className="glass p-6 rounded-major shadow-glass stack-gap">
                    <div className="flex flex-col gap-3">
                        <div className="flex items-center gap-2">
                            <Globe2 className="size-5 jade" aria-hidden="true" />
                            <span className="gc-subheading text-[rgba(247,244,239,0.75)]">Explore</span>
                        </div>
                        <h1 className="gc-heading text-3xl sm:text-4xl jade">All Guardians</h1>
                        <p className="text-muted">
                            Discover Guardians making a positive impact. Search, filter by tags, and explore featured work across the globe.
                        </p>
                    </div>

                    {/* Controls */}
                    <div className="mt-4 grid grid-cols-1 lg:grid-cols-12 gap-3">
                        {/* Search */}
                        <div className="lg:col-span-6">
                            <div className="flex items-center gap-2 glass rounded-minor px-3 py-2">
                                <Search className="size-4 icon-muted" aria-hidden="true" />
                                <input
                                    className="input flex-1 bg-transparent border-0 focus:ring-0"
                                    placeholder="Search by name..."
                                    value={search}
                                    onChange={e => setSearch(e.target.value)}
                                    aria-label="Search guardians by name"
                                />
                                {search && (
                                    <button
                                        className="btn btn--secondary px-2"
                                        onClick={() => setSearch("")}
                                        aria-label="Clear search"
                                        type="button"
                                    >
                                        <XIcon className="size-4" />
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Scope toggle */}
                        <div className="lg:col-span-3">
                            <div className="glass rounded-minor p-1 flex">
                                <button
                                    type="button"
                                    className={cn(
                                        "flex-1 px-3 py-2 rounded-minor text-sm transition",
                                        scope === "all" ? "btn btn--secondary jade translate-y-[-1px]" : "text-[rgba(247,244,239,0.8)]"
                                    )}
                                    onClick={() => setScope("all")}
                                    aria-pressed={scope === "all"}
                                >
                                    All public
                                </button>
                                <button
                                    type="button"
                                    className={cn(
                                        "flex-1 px-3 py-2 rounded-minor text-sm transition",
                                        scope === "mine" ? "btn btn--secondary jade translate-y-[-1px]" : "text-[rgba(247,244,239,0.8)]",
                                        !canScopeMine && "opacity-50 cursor-not-allowed"
                                    )}
                                    onClick={() => canScopeMine && setScope("mine")}
                                    aria-pressed={scope === "mine"}
                                    aria-disabled={!canScopeMine}
                                >
                                    My account
                                </button>
                            </div>
                        </div>

                        {/* Sort */}
                        <div className="lg:col-span-3">
                            <div className="glass rounded-minor px-3 py-2 flex items-center gap-2">
                                <SlidersHorizontal className="size-4 icon-muted" aria-hidden="true" />
                                <label className="text-sm text-subtle" htmlFor="sort-select">Sort</label>
                                <select
                                    id="sort-select"
                                    className="bg-transparent flex-1 outline-none focus:ring-0"
                                    value={sortKey}
                                    onChange={e => setSortKey(e.target.value as SortKey)}
                                    aria-label="Sort guardians"
                                >
                                    <option value="featured">Featured first</option>
                                    <option value="recent">Recently added</option>
                                    <option value="name">Name A–Z</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* Tag filters */}
                    {availableTags.length > 0 && (
                        <div className="mt-3 glass rounded-minor p-3">
                            <div className="flex items-center justify-between gap-3 flex-wrap">
                                <div className="flex items-center gap-2">
                                    <Sparkles className="size-4 icon-muted" aria-hidden="true" />
                                    <span className="text-sm text-subtle">Filter by tags</span>
                                </div>
                                <div className="text-xs text-subtle">
                                    {resultsCount} result{resultsCount === 1 ? "" : "s"}
                                </div>
                            </div>
                            <div className="mt-2 flex flex-wrap gap-2">
                                {availableTags.map(tag => {
                                    const active = selectedTags.includes(tag);
                                    return (
                                        <button
                                            type="button"
                                            key={tag}
                                            onClick={() => toggleTag(tag)}
                                            className={cn("tag-pill", active && "jade border-[rgba(0,196,122,0.5)]")}
                                            aria-pressed={active}
                                        >
                                            {tag}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Clear all */}
                    {(search || selectedTags.length > 0 || sortKey !== "featured" || (canScopeMine && scope !== "all")) && (
                        <div className="pt-2">
                            <button className="btn btn--secondary" onClick={clearAll} type="button">
                                Clear all
                            </button>
                        </div>
                    )}
                </div>
            </section>

            {/* Results */}
            <section className="stack-gap">
                {loading ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {Array.from({ length: 6 }).map((_, i) => (
                            <GuardianCardSkeleton key={i} />
                        ))}
                    </div>
                ) : resultsCount > 0 ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {sorted.map(g => {
                            // Only show status chips for account members when item is not approved
                            const isMemberOwner = !!account && g.account_id === account.id;
                            const showStatus = isMemberOwner && (g.status?.toLowerCase?.() !== "approved");
                            return <GuardianCard key={g.id} guardian={g} showStatus={showStatus} />;
                        })}
                    </div>
                ) : (
                    <div className="glass p-8 rounded-major shadow-glass stack-gap items-center text-center">
                        <div className="flex justify-center">
                            <Globe2 className="size-8 jade" aria-hidden="true" />
                        </div>
                        <h2 className="gc-heading text-2xl">No Guardians found</h2>
                        <p className="text-muted">
                            Try adjusting your search or filters. You can also explore public Guardians across all accounts.
                        </p>
                        <div className="flex flex-wrap gap-3 justify-center">
                            <button className="btn btn--secondary" onClick={clearAll} type="button">
                                Reset filters
                            </button>
                            {isSignedIn ? (
                                <Link className="btn btn--primary" href="/create-guardian">
                                    Add Your Guardian
                                </Link>
                            ) : (
                                <Link className="btn btn--primary" href="/register">
                                    Add Your Guardian
                                </Link>
                            )}
                        </div>
                    </div>
                )}
            </section>
        </div>
    );
}